import { useProPromoBanner } from '@/hooks/useProPromoBanner';
import { PRO_PROMO_FALL26 } from '@/lib/constants';
import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Image,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// Mirrors the WordPress "OS Pro Promo - Site Banner - Fall '26" Kadence
// conversion (post 1630456): a gold band with the "OS Pro New Season" thumbnail
// on the left and, on the right, a dark "— LIMITED TIME —" pill above the
// underlined headline. The WP version has a thin black strip above the gold
// band; that reads as dead space in the app, so it's dropped here (same call
// as the Summer Sale banner).
const COLORS = {
  band: '#c1860f',
  pillBg: 'rgba(0,0,0,0.14)',
  pillText: '#011c23',
  headline: '#0A1A2E',
  clockBox: '#011c23',
  clockDigits: '#ffffff',
  clockSeconds: '#ff3d00',
} as const;

// Thumbnail intrinsic 288×192 (3:2); WP renders it 96×64, 72×48 under 600px,
// 60×40 under 380px.
const IMAGE_ASPECT_RATIO = 3 / 2;

// WP breakpoints for the compact / tiny layouts.
const COMPACT_MAX_WIDTH = 600;
const TINY_MAX_WIDTH = 380;

const LABEL = "Limited time — Don't Miss This Rare OS Pro Offer. Learn More";

const CLOCK_MONO_FONT = "'Courier New', Courier, monospace";

function pad(n: number): string {
  return String(Math.max(0, n)).padStart(2, '0');
}

function remainingSeconds(): number {
  return Math.max(0, Math.floor((PRO_PROMO_FALL26.COUNTDOWN_TARGET_MS - Date.now()) / 1000));
}

type ClockSize = 'full' | 'compact' | 'tiny';

function ClockUnit({
  value,
  label,
  size,
  isSeconds,
  opacity,
}: {
  value: number;
  label: string;
  size: ClockSize;
  isSeconds?: boolean;
  opacity?: Animated.Value;
}) {
  return (
    <View style={clockStyles.unit}>
      <Animated.Text
        style={[
          clockStyles.num,
          size === 'compact' && clockStyles.numCompact,
          size === 'tiny' && clockStyles.numTiny,
          isSeconds && clockStyles.numSeconds,
          opacity != null && { opacity },
        ]}
      >
        {pad(value)}
      </Animated.Text>
      <Text style={clockStyles.label}>{label}</Text>
    </View>
  );
}

function ClockColon({ size }: { size: ClockSize }) {
  return (
    <Text
      style={[clockStyles.colon, size !== 'full' && clockStyles.colonCompact]}
      accessibilityElementsHidden
    >
      :
    </Text>
  );
}

/**
 * DAYS:HOURS:MINS:SECS countdown to the offer end, ticking every second.
 * Mirrors Ben's WP timer markup (.osj-bt): dark boxes, white digits, orange
 * seconds with a .15s fade on each tick.
 */
function CountdownClock({ size }: { size: ClockSize }) {
  const [remaining, setRemaining] = useState(remainingSeconds);
  const secondsOpacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const id = setInterval(() => setRemaining(remainingSeconds()), 1000);
    return () => clearInterval(id);
  }, []);

  const secs = remaining % 60;

  useEffect(() => {
    secondsOpacity.setValue(0.5);
    Animated.timing(secondsOpacity, { toValue: 1, duration: 150, useNativeDriver: true }).start();
  }, [secs, secondsOpacity]);

  return (
    <View style={clockStyles.clock} accessibilityRole="timer" accessibilityLabel="Offer countdown">
      <ClockUnit value={Math.floor(remaining / 86400)} label="days" size={size} />
      <ClockColon size={size} />
      <ClockUnit value={Math.floor((remaining % 86400) / 3600)} label="hours" size={size} />
      <ClockColon size={size} />
      <ClockUnit value={Math.floor((remaining % 3600) / 60)} label="mins" size={size} />
      <ClockColon size={size} />
      <ClockUnit value={secs} label="secs" size={size} isSeconds opacity={secondsOpacity} />
    </View>
  );
}

/**
 * Fall '26 OS Pro promo banner, mounted above the app chrome on every
 * authenticated page, web and iOS. Audience, live window, tap routing and
 * dismissal rules live in useProPromoBanner; this renders nothing when the
 * gate isn't met, so it's safe to mount unconditionally. The X hides it for
 * good on this browser / device.
 */
export function ProPromoBanner() {
  const banner = useProPromoBanner();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();

  if (!banner) return null;

  const tiny = width <= TINY_MAX_WIDTH;
  const compact = width <= COMPACT_MAX_WIDTH;
  const imageHeight = tiny ? 40 : compact ? 48 : 64;
  const headlineSize = tiny ? 15 : compact ? 17 : 20;
  const clockSize: ClockSize = tiny ? 'tiny' : compact ? 'compact' : 'full';

  return (
    <View style={{ backgroundColor: COLORS.band, paddingTop: insets.top }}>
      <Pressable
        onPress={banner.open}
        accessibilityRole="link"
        accessibilityLabel={LABEL}
        style={[styles.band, compact && styles.bandCompact, tiny && styles.bandTiny]}
      >
        <Image
          source={{ uri: PRO_PROMO_FALL26.IMAGE_URL }}
          style={[styles.image, { height: imageHeight, width: imageHeight * IMAGE_ASPECT_RATIO }]}
          resizeMode="cover"
          accessibilityLabel="OS Pro New Season"
        />
        <View style={styles.copy}>
          {banner.countdown ? (
            <CountdownClock size={clockSize} />
          ) : (
            <View style={styles.pill}>
              <Text style={styles.pillText}>— Limited Time —</Text>
            </View>
          )}
          <Text style={[styles.headline, { fontSize: headlineSize }]}>
            Don&apos;t Miss This Rare <Text style={styles.headlineItalic}>OS Pro</Text> Offer.
            Learn More →
          </Text>
        </View>
      </Pressable>
      <Pressable
        onPress={banner.dismiss}
        accessibilityRole="button"
        accessibilityLabel="Dismiss OS Pro offer banner"
        hitSlop={8}
        style={[styles.closeButton, { top: insets.top }]}
      >
        <Ionicons name="close" size={20} color={COLORS.headline} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  band: {
    paddingVertical: 14,
    paddingLeft: 40,
    // Extra right padding keeps the centered copy clear of the close button.
    paddingRight: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },
  bandCompact: {
    paddingVertical: 10,
    paddingLeft: 10,
    paddingRight: 40,
    gap: 10,
  },
  bandTiny: {
    paddingLeft: 8,
    gap: 8,
  },
  image: {
    borderRadius: 8,
    flexShrink: 0,
  },
  copy: {
    flexShrink: 1,
    alignItems: 'flex-start',
    gap: 4,
  },
  pill: {
    backgroundColor: COLORS.pillBg,
    paddingVertical: 3,
    paddingHorizontal: 12,
    borderRadius: 20,
  },
  pillText: {
    color: COLORS.pillText,
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 0.96,
    textTransform: 'uppercase',
  },
  headline: {
    color: COLORS.headline,
    fontFamily: 'Inter',
    fontWeight: '700',
    lineHeight: 25,
    textDecorationLine: 'underline',
  },
  headlineItalic: {
    fontStyle: 'italic',
  },
  closeButton: {
    position: 'absolute',
    right: 10,
    bottom: 0,
    justifyContent: 'center',
  },
});

const clockStyles = StyleSheet.create({
  clock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    marginBottom: 2,
  },
  unit: {
    alignItems: 'center',
    gap: 2,
  },
  num: {
    backgroundColor: COLORS.clockBox,
    color: COLORS.clockDigits,
    fontSize: 18,
    fontWeight: '700',
    fontFamily: CLOCK_MONO_FONT,
    width: 36,
    height: 34,
    lineHeight: 34,
    textAlign: 'center',
    borderRadius: 6,
    overflow: 'hidden',
  },
  numCompact: {
    fontSize: 16,
    width: 32,
    height: 30,
    lineHeight: 30,
  },
  numTiny: {
    fontSize: 15,
    width: 29,
    height: 28,
    lineHeight: 28,
  },
  numSeconds: {
    color: COLORS.clockSeconds,
  },
  label: {
    fontSize: 8,
    fontWeight: '700',
    fontFamily: 'Inter',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: COLORS.pillText,
    opacity: 0.75,
  },
  colon: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.pillText,
    opacity: 0.5,
    paddingBottom: 12,
    lineHeight: 18,
  },
  colonCompact: {
    fontSize: 16,
    lineHeight: 16,
    paddingBottom: 11,
  },
});
